import { useCallback } from "react";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import { getRequest, postRequest } from "lib/core/apiClient";
import { getItems, appendParamsToItemsUrl } from "lib/utils/checkout";
import { errorToast } from "lib/utils/toasters";
import { useLocale } from "lib/hooks/useLocale";
import { ShippingHandlesType } from "lib/types/checkout";
import { mutate } from "swr";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";

export const useApplyDiscount = () => {
  const { t } = useLocale();
  const { openPopup } = usePopupOrchestrator();
  const {
    state: { isAuthenticated },
  } = useAuthContext();
  const {
    state: { checkoutMetadata },
  } = useMerchantContext();
  const {
    state: { checkoutId, checkoutModal, checkoutView  },
    actions: {
      updateCheckoutBasedOnCheckoutResponse,
      setCoupons,
      setCheckoutItems,
      setShippingHandles,
      setCheckoutModal,
    },
  } = useCheckoutContext();
  const {
    actions: { mutatePayment },
  } = usePaymentContext();
  const { initialCheckoutStep } = checkoutMetadata ?? {};
  const applyDiscount = useCallback(
    async (couponCode: string): Promise<boolean> => {
      if (!couponCode?.trim() || !checkoutId) return false;

      const apiType = isAuthenticated ? "KRATOS_PRIVATE" : "KRATOS_PUBLIC";
      const DISCOUNT_PATH = appendParamsToItemsUrl(`/checkout/${checkoutId}/discount`);

      try {
        const response = await postRequest(DISCOUNT_PATH, { discount_code: couponCode.trim() }, apiType);

        if (!response) {
          errorToast(t("coupon_not_found"));
          return false;
        }

        const discountResponse = await getRequest(DISCOUNT_PATH, apiType);
        setCoupons(discountResponse);
        updateCheckoutBasedOnCheckoutResponse(response);

        if (response?.items) {
          setCheckoutItems(getItems(response.items));
        }

        const shippingHandles = (response?.metadata?.available_shipping_handles ?? []) as ShippingHandlesType;
        setShippingHandles(shippingHandles);
        if (
          shippingHandles.length > 0 &&
          Boolean(response?.metadata?.show_shipping_handle_selector) &&
          checkoutView === "PAYMENTS" &&
          initialCheckoutStep !== "PAYMENTS"
        ) {
          openPopup("SHIPPING_HANDLES", {});
        } else {
          mutatePayment();
          mutate(`/checkout/${checkoutId}/rewards`);
          mutate("UPI_INTENT");
        }
        return true;
      } catch (e: any) {
        errorToast(e?.response?.data?.error?.message ?? t("coupon_not_found"));
        return false;
      }
    },
    [
      checkoutId,
      isAuthenticated,
      mutatePayment,
      setCoupons,
      setCheckoutItems,
      setShippingHandles,
      setCheckoutModal,
      updateCheckoutBasedOnCheckoutResponse,
      t,
    ],
  );

  return { applyDiscount };
};
